import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  MapPinned, Nfc, KeyRound, Loader2, CheckCircle2, MapPin, ShieldCheck,
  Crosshair, Save, ClipboardList, Navigation, CalendarClock, Route, FileText, FileSpreadsheet, Trash2, Plus, CheckCircle,
} from "lucide-react";

const fmtDT = (s) => { try { return new Date(s).toLocaleString("tr-TR"); } catch { return s; } };

async function downloadFile(url, filename) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([res.data]));
    a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  } catch { toast.error("İndirilemedi"); }
}

function getGeo() {
  return new Promise((res) => {
    if (!navigator.geolocation) return res(null);
    navigator.geolocation.getCurrentPosition(
      (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => res(null), { timeout: 6000, enableHighAccuracy: true });
  });
}

export default function FieldVisits() {
  const { can } = useAuth();
  const canAudit = can("visit.read") || can("approve.admin") || can("approve.chain") || can("report.read");
  const canConfig = can("fieldops.manage") || can("location.manage") || can("approve.admin");
  const [tab, setTab] = useState("checkin");

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6" data-testid="field-visits-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><MapPinned className="h-6 w-6 text-brand-green" /> Saha Ziyaret & Denetim</h1>
        <p className="text-sm text-slate-500">Bölge sorumlusu mağaza/depo/markette firma kartını (NFC) veya kodu okutur; nerede olduğu kayda geçer.</p>
      </div>
      <div className="flex flex-wrap gap-2 border-b border-slate-200">
        <TabBtn id="checkin" cur={tab} set={setTab} icon={Nfc} label="Ziyaret Yap" testId="fv-tab-checkin" />
        {canAudit && <TabBtn id="log" cur={tab} set={setTab} icon={ClipboardList} label="Ziyaret Kayıtları" testId="fv-tab-log" />}
        {canConfig && <TabBtn id="config" cur={tab} set={setTab} icon={ShieldCheck} label="Lokasyon Ayarları" testId="fv-tab-config" />}
        <TabBtn id="program" cur={tab} set={setTab} icon={Route} label="Ziyaret Programı" testId="fv-tab-program" />
      </div>
      {tab === "checkin" && <CheckIn />}
      {tab === "log" && canAudit && <VisitLog />}
      {tab === "config" && canConfig && <LocationConfig />}
      {tab === "program" && <VisitProgram canConfig={canConfig} />}
    </div>
  );
}

function TabBtn({ id, cur, set, icon: Icon, label, testId }) {
  const active = cur === id;
  return (
    <button data-testid={testId} onClick={() => set(id)}
      className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${active ? "border-brand-green text-brand-green" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function CheckIn() {
  const [locations, setLocations] = useState([]);
  const [code, setCode] = useState("");
  const [locId, setLocId] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [mine, setMine] = useState([]);

  const loadMine = useCallback(() => { api.get("/field-visits").then((r) => setMine((r.data || []).slice(0, 8))).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/field-visits/locations").then((r) => setLocations(r.data || [])).catch(() => {});
    loadMine();
  }, [loadMine]);

  const doCheckin = async (payload) => {
    setBusy(true);
    try {
      const geo = await getGeo();
      const { data } = await api.post("/field-visits/checkin", { ...payload, geo });
      setLast(data);
      toast.success(`${data.location_name} — ziyaret kaydedildi${data.distance_m != null ? ` (${Math.round(data.distance_m)}m)` : ""}`);
      setCode(""); setLocId(""); loadMine();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kayıt başarısız"); }
    finally { setBusy(false); }
  };

  const scanNfc = async () => {
    if (!("NDEFReader" in window)) { toast.error("Bu cihaz/tarayıcı NFC desteklemiyor — Kod ile giriş yapın (Android Chrome gerekir)"); return; }
    try {
      const reader = new window.NDEFReader();
      await reader.scan();
      toast.info("Firma kartını/etiketini telefonun arkasına yaklaştırın…");
      reader.onreading = (event) => doCheckin({ nfc_tag_id: event.serialNumber, method: "nfc" });
      reader.onreadingerror = () => toast.error("Kart okunamadı, tekrar deneyin");
    } catch (e) { toast.error("NFC başlatılamadı: " + (e?.message || "")); }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Nfc className="h-4 w-4 text-brand-green" /> NFC / Firma Kartı ile Okut</div>
          <Button className="w-full py-6 text-base" onClick={scanNfc} disabled={busy} data-testid="fv-nfc-btn">
            <Nfc className="mr-2 h-5 w-5" /> Kartı Okut (NFC)
          </Button>
          <p className="mt-2 text-xs text-slate-400">NFC yalnız Android Chrome'da çalışır. Desteklenmiyorsa aşağıdan kod ile giriş yapın.</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><KeyRound className="h-4 w-4 text-brand-green" /> Kod ile Ziyaret</div>
          <div className="flex gap-2">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Lokasyon kodu (örn. ZIYARET1 / DP-0004)" data-testid="fv-code-input" />
            <Button onClick={() => doCheckin({ visit_code: code, method: "code" })} disabled={busy || !code.trim()} data-testid="fv-code-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Giriş"}
            </Button>
          </div>
          <div className="mt-3">
            <Label className="text-xs">veya listeden seç</Label>
            <div className="mt-1 flex gap-2">
              <select value={locId} onChange={(e) => setLocId(e.target.value)} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="fv-loc-select">
                <option value="">Lokasyon seç…</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name} · {l.type_label}</option>)}
              </select>
              <Button variant="outline" onClick={() => doCheckin({ location_id: locId, method: "manual" })} disabled={busy || !locId} data-testid="fv-loc-btn"><MapPin className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
        {last && (
          <div className="flex items-center gap-3 rounded-lg bg-emerald-50 p-4 text-emerald-800" data-testid="fv-last">
            <CheckCircle2 className="h-6 w-6" />
            <div><div className="font-semibold">{last.location_name}</div>
              <div className="text-xs">{fmtDT(last.at)} · {last.method.toUpperCase()}{last.distance_m != null ? ` · ${Math.round(last.distance_m)}m ${last.within_geofence ? "(konumda ✓)" : "(uzak!)"}` : ""}</div>
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="mb-2 text-sm font-semibold text-slate-800">Son Ziyaretlerim</div>
        <div className="space-y-2">
          {mine.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">Henüz ziyaret kaydınız yok.</div>}
          {mine.map((v) => (
            <div key={v.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm" data-testid={`fv-mine-${v.id}`}>
              <div><div className="font-medium text-slate-800">{v.location_name}</div><div className="text-xs text-slate-400">{fmtDT(v.at)} · {v.method}</div></div>
              {v.distance_m != null && <span className={`text-xs font-medium ${v.within_geofence ? "text-emerald-600" : "text-red-500"}`}>{Math.round(v.distance_m)}m</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function VisitLog() {
  const [rows, setRows] = useState([]);
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get("/field-visits", { params: date ? { date } : {} }).then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [date]);
  return (
    <div className="space-y-3" data-testid="fv-log">
      <div className="flex items-end gap-2">
        <div><label className="block text-xs text-slate-500">Tarih (opsiyonel)</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-testid="fv-log-date" /></div>
        {date && <Button size="sm" variant="ghost" onClick={() => setDate("")}>Tümü</Button>}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => downloadFile(`/field-visits/export?fmt=pdf${date ? `&date=${date}` : ""}`, `saha-ziyaret-${date || "tum"}.pdf`)}
            className="flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50" data-testid="fv-log-pdf"><FileText className="h-3.5 w-3.5 text-red-500" /> PDF</button>
          <button onClick={() => downloadFile(`/field-visits/export?fmt=xlsx${date ? `&date=${date}` : ""}`, `saha-ziyaret-${date || "tum"}.xlsx`)}
            className="flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50" data-testid="fv-log-xlsx"><FileSpreadsheet className="h-3.5 w-3.5 text-emerald-600" /> Excel</button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        {loading ? <div className="py-12 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-300" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-400"><tr><th className="px-3 py-2">Sorumlu</th><th>Lokasyon</th><th>Zaman</th><th>Yöntem</th><th className="text-right">Konum</th></tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan="5" className="py-10 text-center text-slate-400">Kayıt yok.</td></tr>}
              {rows.map((v) => (
                <tr key={v.id} className="border-t border-slate-50" data-testid={`fv-log-row-${v.id}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{v.supervisor_name}</td>
                  <td className="text-slate-600">{v.location_name}<span className="ml-1 text-xs text-slate-400">{v.district || v.province || ""}</span></td>
                  <td className="text-slate-500">{fmtDT(v.at)}</td>
                  <td><span className="rounded bg-slate-100 px-1.5 text-xs uppercase text-slate-600">{v.method}</span></td>
                  <td className="text-right">{v.distance_m == null ? <span className="text-slate-300">—</span> : <span className={v.within_geofence ? "text-emerald-600" : "text-red-500"}>{Math.round(v.distance_m)}m {v.within_geofence ? "✓" : "!"}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function LocationConfig() {
  const [locs, setLocs] = useState([]);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState("");
  const load = useCallback(() => { api.get("/field-visits/locations").then((r) => setLocs(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const captureHere = async (id) => {
    const geo = await getGeo();
    if (!geo) { toast.error("Konum alınamadı — konum izni verin"); return; }
    setEdit((e) => ({ ...e, [id]: { ...(e[id] || {}), lat: geo.lat.toFixed(6), lng: geo.lng.toFixed(6) } }));
    toast.success("Konum alındı — kaydetmeyi unutmayın");
  };
  const field = (l, k) => (edit[l.id]?.[k] ?? l[k] ?? "");
  const setF = (id, k, v) => setEdit((e) => ({ ...e, [id]: { ...(e[id] || {}), [k]: v } }));

  const save = async (l) => {
    setBusy(l.id);
    try {
      const e = edit[l.id] || {};
      await api.put(`/field-visits/locations/${l.id}/config`, {
        lat: e.lat != null && e.lat !== "" ? Number(e.lat) : l.lat,
        lng: e.lng != null && e.lng !== "" ? Number(e.lng) : l.lng,
        radius_m: e.radius_m != null && e.radius_m !== "" ? Number(e.radius_m) : l.radius_m,
        visit_code: e.visit_code ?? l.visit_code,
        nfc_tag_id: e.nfc_tag_id ?? l.nfc_tag_id,
      });
      toast.success(`${l.name} ayarları kaydedildi`); setEdit((s) => { const n = { ...s }; delete n[l.id]; return n; }); load();
    } catch (er) { toast.error(er?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-3" data-testid="fv-config">
      <p className="text-xs text-slate-500">Her lokasyona kod / NFC etiket kimliği ve konum (geofence) tanımlayın. Konumu ilgili lokasyondayken "Buradan Al" ile alabilirsiniz.</p>
      {locs.map((l) => (
        <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`fv-config-${l.id}`}>
          <div className="mb-2 flex items-center justify-between">
            <div className="font-medium text-slate-800">{l.name} <span className="ml-1 text-xs text-slate-400">{l.type_label}</span></div>
            <Button size="sm" onClick={() => save(l)} disabled={busy === l.id} data-testid={`fv-config-save-${l.id}`}>{busy === l.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="mr-1 h-3.5 w-3.5" /> Kaydet</>}</Button>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <div><Label className="text-xs">Ziyaret Kodu</Label><Input value={field(l, "visit_code")} onChange={(e) => setF(l.id, "visit_code", e.target.value)} data-testid={`fv-cfg-code-${l.id}`} /></div>
            <div><Label className="text-xs">NFC Etiket ID</Label><Input value={field(l, "nfc_tag_id")} onChange={(e) => setF(l.id, "nfc_tag_id", e.target.value)} placeholder="kart seri no" /></div>
            <div><Label className="text-xs">Yarıçap (m)</Label><Input type="number" value={field(l, "radius_m")} onChange={(e) => setF(l.id, "radius_m", e.target.value)} /></div>
            <div className="flex items-end"><Button variant="outline" size="sm" className="w-full" onClick={() => captureHere(l.id)}><Crosshair className="mr-1 h-4 w-4" /> Konumu Buradan Al</Button></div>
            <div><Label className="text-xs">Enlem (lat)</Label><Input value={field(l, "lat")} onChange={(e) => setF(l.id, "lat", e.target.value)} /></div>
            <div><Label className="text-xs">Boylam (lng)</Label><Input value={field(l, "lng")} onChange={(e) => setF(l.id, "lng", e.target.value)} /></div>
            {l.lat != null && <div className="col-span-2 flex items-end text-xs text-emerald-600"><Navigation className="mr-1 h-3.5 w-3.5" /> Konum tanımlı</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function VisitProgram({ canConfig }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [locs, setLocs] = useState([]);
  const [supers, setSupers] = useState([]);
  const [supEmail, setSupEmail] = useState("");
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [real, setReal] = useState([]);

  const loadReal = useCallback(() => {
    api.get("/field-visits/plans/realization", { params: { date } }).then((r) => setReal(r.data?.rows || [])).catch(() => setReal([]));
  }, [date]);
  useEffect(() => {
    api.get("/field-visits/locations").then((r) => setLocs(r.data || [])).catch(() => {});
    if (canConfig) api.get("/users").then((r) => setSupers((r.data || []).filter((u) => u.role === "region_supervisor"))).catch(() => {});
  }, [canConfig]);
  useEffect(() => { loadReal(); }, [loadReal]);

  const toggle = (lid) => setSel((s) => s.includes(lid) ? s.filter((x) => x !== lid) : [...s, lid]);
  const create = async () => {
    if (!supEmail) { toast.error("Sorumlu e-postası girin/seçin"); return; }
    if (!sel.length) { toast.error("En az bir lokasyon seçin"); return; }
    setBusy(true);
    try {
      await api.post("/field-visits/plans", { supervisor_email: supEmail, date, location_ids: sel });
      toast.success("Ziyaret programı oluşturuldu"); setSel([]); loadReal();
    } catch (e) { toast.error(e?.response?.data?.detail || "Oluşturulamadı"); }
    finally { setBusy(false); }
  };
  const rateColor = (r) => r >= 80 ? "text-emerald-600" : r >= 50 ? "text-amber-600" : "text-red-600";

  return (
    <div className="space-y-4" data-testid="fv-program">
      {canConfig && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Route className="h-4 w-4 text-brand-green" /> Günlük Ziyaret Programı Tanımla</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label className="text-xs">Bölge Sorumlusu</Label>
              {supers.length ? (
                <select value={supEmail} onChange={(e) => setSupEmail(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="fv-prog-sup">
                  <option value="">Seç…</option>
                  {supers.map((u) => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
                </select>
              ) : <Input value={supEmail} onChange={(e) => setSupEmail(e.target.value)} placeholder="sorumlu@firma.com" className="mt-1" data-testid="fv-prog-sup" />}
            </div>
            <div><Label className="text-xs">Tarih</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="fv-prog-date" /></div>
          </div>
          <Label className="mt-3 block text-xs">Ziyaret edilecek lokasyonlar ({sel.length} seçili)</Label>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {locs.map((l) => {
              const on = sel.includes(l.id);
              return (
                <button key={l.id} type="button" onClick={() => toggle(l.id)} data-testid={`fv-prog-loc-${l.id}`}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${on ? "bg-brand-green text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{l.name}</button>
              );
            })}
          </div>
          <Button className="mt-3" onClick={create} disabled={busy} data-testid="fv-prog-create">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />} Program Oluştur</Button>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><CalendarClock className="h-4 w-4 text-brand-green" /> Gerçekleşme Oranı</div>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1 text-sm" data-testid="fv-prog-real-date" />
        </div>
        {real.length === 0 ? <p className="py-4 text-center text-sm text-slate-400">Bu tarih için tanımlı program yok.</p> : (
          <div className="space-y-2" data-testid="fv-prog-real">
            {real.map((r) => (
              <div key={r.supervisor_email} className="rounded-lg border border-slate-100 p-3" data-testid={`fv-real-${r.supervisor_email}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-800">{r.supervisor_name}</span>
                  <span className={`text-sm font-bold ${rateColor(r.rate)}`}>%{r.rate} <span className="text-xs font-normal text-slate-400">({r.visited}/{r.planned})</span></span>
                </div>
                {r.missed?.length > 0 && <div className="mt-1 flex items-start gap-1 text-xs text-red-600"><Trash2 className="mt-0.5 h-3 w-3" /> Ziyaret edilmeyen: {r.missed.map((m) => m.name).join(", ")}</div>}
                {r.done?.length > 0 && <div className="mt-0.5 flex items-start gap-1 text-xs text-emerald-600"><CheckCircle className="mt-0.5 h-3 w-3" /> {r.done.map((m) => m.name).join(", ")}</div>}
                {r.extra > 0 && <div className="text-xs text-slate-400">Program dışı ekstra ziyaret: {r.extra}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

